import React, { useState } from 'react';
import { Wifi, WifiOff, AlertCircle, CheckCircle2, Clock, Send, FileText, User, Phone, MapPin, Calendar, Home, ShoppingBag, Gift, MessageCircle, Menu, X, Headphones, Mail, ChevronLeft, ChevronRight } from 'lucide-react';
import './App.css';

export default function App() {
  const [formData, setFormData] = useState({
    name: '',
    phone: '',
    address: '',
    issueType: '',
    description: '',
    priority: 'medium'
  });
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);
  const [complaints, setComplaints] = useState([]);
  const [errors, setErrors] = useState({});
  const [activeTab, setActiveTab] = useState('home');
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [currentSlide, setCurrentSlide] = useState(0);
  const [touchStart, setTouchStart] = useState(0);
  const [touchEnd, setTouchEnd] = useState(0);

  const banners = [
    {
      title: 'Lightning Fast Internet',
      subtitle: 'Up to 1000 Mbps Speed',
      bg: 'from-blue-600 via-blue-500 to-cyan-500',
      image: 'https://images.unsplash.com/photo-1451187580459-43490279c0fa?w=800&q=80'
    },
    {
      title: 'Special Offer',
      subtitle: 'For 10 months recharge get 2 months free',
      bg: 'from-purple-600 via-pink-500 to-pink-600',
      image: 'https://images.unsplash.com/photo-1557804506-669a67965ba0?w=800&q=80'
    },
    {
      title: '24/7 Support',
      subtitle: 'Always Here to Help',
      bg: 'from-green-600 via-emerald-500 to-teal-500',
      image: 'https://images.unsplash.com/photo-1516321318423-f06f85e504b3?w=800&q=80'
    }
  ];

  const issueTypes = [
    { value: 'no-internet', label: 'No Internet Connection', icon: WifiOff },
    { value: 'slow-speed', label: 'Slow Speed', icon: Wifi },
    { value: 'frequent-disconnection', label: 'Frequent Disconnection', icon: AlertCircle },
    { value: 'router-issue', label: 'Router Issue', icon: AlertCircle },
    { value: 'billing', label: 'Billing Issue', icon: FileText },
    { value: 'other', label: 'Other', icon: AlertCircle }
  ];

  React.useEffect(() => {
    const timer = setInterval(() => {
      setCurrentSlide((prev) => (prev + 1) % banners.length);
    }, 4000);
    return () => clearInterval(timer);
  }, [banners.length]);

  const nextSlide = () => {
    setCurrentSlide((prev) => (prev + 1) % banners.length);
  };

  const prevSlide = () => {
    setCurrentSlide((prev) => (prev - 1 + banners.length) % banners.length);
  };

  const handleTouchStart = (e) => {
    setTouchStart(e.targetTouches[0].clientX);
  };

  const handleTouchMove = (e) => {
    setTouchEnd(e.targetTouches[0].clientX);
  };

  const handleTouchEnd = () => {
    if (!touchStart || !touchEnd) return;
    
    const distance = touchStart - touchEnd;
    const isLeftSwipe = distance > 50;
    const isRightSwipe = distance < -50;

    if (isLeftSwipe) {
      nextSlide();
    }
    if (isRightSwipe) {
      prevSlide();
    }

    setTouchStart(0);
    setTouchEnd(0);
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.name.trim()) {
      newErrors.name = 'Name is required';
    } else if (formData.name.trim().length < 3) {
      newErrors.name = 'Name must be at least 3 characters';
    }

    if (!formData.phone.trim()) {
      newErrors.phone = 'Phone number is required';
    } else if (!/^[0-9]{10}$/.test(formData.phone.replace(/\s/g, ''))) {
      newErrors.phone = 'Please enter a valid 10-digit phone number';
    }

    if (!formData.address.trim()) {
      newErrors.address = 'Address is required';
    } else if (formData.address.trim().length < 10) {
      newErrors.address = 'Please enter a complete address';
    }

    

 

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    setLoading(true);

    const GOOGLE_SCRIPT_URL = 'https://script.google.com/macros/s/AKfycbyp3KdkgK9AqrCNi1K9Imjd9WJP09HJrdH50MnnG5qP4JupofdcPrm51nGAb7lTCAgn9A/exec';
    
    try {
      const complaintData = {
        name: formData.name,
        phone: formData.phone,
        address: formData.address,
        issueType: formData.issueType,
        description: formData.description,
        priority: formData.priority,
        timestamp: new Date().toLocaleString('en-IN', { timeZone: 'Asia/Kolkata' }),
        status: 'pending'
      };

      fetch(GOOGLE_SCRIPT_URL, {
        method: 'POST',
        mode: 'no-cors',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(complaintData)
      }).catch(err => console.log('Sheet update queued:', err));

      const newComplaint = {
        id: Date.now(),
        ...complaintData
      };
      
      setComplaints(prev => [newComplaint, ...prev]);
      setSubmitted(true);
      setLoading(false);
      setErrors({});

      setTimeout(() => {
        setSubmitted(false);
        setFormData({
          name: '',
          phone: '',
          address: '',
          issueType: '',
          description: '',
          priority: 'medium'
        });
      }, 5000);

    } catch (error) {
      console.error('Error submitting complaint:', error);
      setLoading(false);
      setErrors({ submit: 'Failed to submit complaint. Please try again.' });
    }
  };

  const getStatusColor = (status) => {
    switch(status) {
      case 'pending': return 'bg-yellow-500';
      case 'in-progress': return 'bg-blue-500';
      case 'resolved': return 'bg-green-500';
      default: return 'bg-gray-500';
    }
  };

  const getPriorityColor = (priority) => {
    switch(priority) {
      case 'high': return 'text-red-500';
      case 'medium': return 'text-yellow-500';
      case 'low': return 'text-green-500';
      default: return 'text-gray-500';
    }
  };

  const renderHome = () => (
    <div className="space-y-6 pb-24">
      <div 
        className="relative overflow-hidden rounded-3xl shadow-2xl h-56"
        onTouchStart={handleTouchStart}
        onTouchMove={handleTouchMove}
        onTouchEnd={handleTouchEnd}
      >
        {banners.map((banner, idx) => (
          <div
            key={idx}
            className={`absolute inset-0 transition-all duration-700 ease-in-out ${
              idx === currentSlide ? 'opacity-100 translate-x-0' : 'opacity-0 translate-x-full'
            }`}
          >
            <div className="relative w-full h-full">
              <div 
                className="absolute inset-0 bg-cover bg-center"
                style={{ backgroundImage: `url(${banner.image})` }}
              />
              <div className={`absolute inset-0 bg-gradient-to-r ${banner.bg} opacity-85`} />
              
              <div className="relative h-full p-6 flex flex-col justify-between">
                <div>
                  <h3 className="text-3xl font-bold text-white mb-2 drop-shadow-lg">{banner.title}</h3>
                  <p className="text-white text-lg drop-shadow-md">{banner.subtitle}</p>
                </div>
                
                <div className="flex gap-2">
                  {banners.map((_, dotIdx) => (
                    <button
                      key={dotIdx}
                      onClick={() => setCurrentSlide(dotIdx)}
                      className={`h-2 rounded-full transition-all ${
                        dotIdx === currentSlide ? 'w-8 bg-white' : 'w-2 bg-white/50'
                      }`}
                    />
                  ))}
                </div>
              </div>
            </div>
          </div>
        ))}

        <button
          onClick={prevSlide}
          className="absolute left-3 top-1/2 -translate-y-1/2 bg-white/20 backdrop-blur-sm hover:bg-white/30 text-white p-2 rounded-full transition-all shadow-lg"
        >
          <ChevronLeft className="w-6 h-6" />
        </button>
        <button
          onClick={nextSlide}
          className="absolute right-3 top-1/2 -translate-y-1/2 bg-white/20 backdrop-blur-sm hover:bg-white/30 text-white p-2 rounded-full transition-all shadow-lg"
        >
          <ChevronRight className="w-6 h-6" />
        </button>
      </div>

      {submitted && (
        <div className="bg-gradient-to-r from-green-500 to-emerald-500 text-white p-6 rounded-3xl shadow-2xl animate-pulse">
          <div className="text-center space-y-3">
            <div className="flex justify-center">
              <div className="bg-white/20 backdrop-blur-sm p-4 rounded-full">
                <CheckCircle2 className="w-12 h-12" />
              </div>
            </div>
            <div>
              <h3 className="text-2xl font-bold mb-2">Thank You! 🎉</h3>
              <p className="text-lg font-semibold">Your Complaint Has Been Submitted</p>
              <p className="text-sm text-green-100 mt-2">
                We've received your complaint and our team will get back to you within 2-4 hours.
              </p>
              <div className="mt-4 bg-white/20 backdrop-blur-sm rounded-xl p-3">
                <p className="text-xs text-white/90">
                  Ticket ID: <span className="font-bold">#{Date.now().toString().slice(-6)}</span>
                </p>
              </div>
            </div>
          </div>
        </div>
      )}

      <div className="bg-white rounded-3xl shadow-xl p-6 space-y-5">
        <div className="flex items-center gap-2 mb-4">
          <FileText className="w-5 h-5 text-indigo-600" />
          <h2 className="text-xl font-bold text-gray-800">New Complaint</h2>
        </div>

        <div className="space-y-4">
          <div className="space-y-2">
            <label className="flex items-center gap-2 text-sm font-medium text-gray-700">
              <User className="w-4 h-4 text-indigo-600" />
              Full Name <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              name="name"
              value={formData.name}
              onChange={handleInputChange}
              required
              className={`w-full px-4 py-3 bg-gray-50 border-2 ${
                errors.name ? 'border-red-300' : 'border-gray-200'
              } rounded-xl focus:border-indigo-500 focus:ring-2 focus:ring-indigo-200 outline-none transition-all`}
              placeholder="Enter your full name"
            />
            {errors.name && (
              <p className="text-red-500 text-xs flex items-center gap-1">
                <AlertCircle className="w-3 h-3" />
                {errors.name}
              </p>
            )}
          </div>

          <div className="space-y-2">
            <label className="flex items-center gap-2 text-sm font-medium text-gray-700">
              <Phone className="w-4 h-4 text-indigo-600" />
              Phone Number <span className="text-red-500">*</span>
            </label>
            <input
              type="tel"
              name="phone"
              value={formData.phone}
              onChange={handleInputChange}
              required
              maxLength="10"
              className={`w-full px-4 py-3 bg-gray-50 border-2 ${
                errors.phone ? 'border-red-300' : 'border-gray-200'
              } rounded-xl focus:border-indigo-500 focus:ring-2 focus:ring-indigo-200 outline-none transition-all`}
              placeholder="Enter 10-digit phone number"
            />
            {errors.phone && (
              <p className="text-red-500 text-xs flex items-center gap-1">
                <AlertCircle className="w-3 h-3" />
                {errors.phone}
              </p>
            )}
          </div>

          <div className="space-y-2">
            <label className="flex items-center gap-2 text-sm font-medium text-gray-700">
              <MapPin className="w-4 h-4 text-indigo-600" />
              Address <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              name="address"
              value={formData.address}
              onChange={handleInputChange}
              required
              className={`w-full px-4 py-3 bg-gray-50 border-2 ${
                errors.address ? 'border-red-300' : 'border-gray-200'
              } rounded-xl focus:border-indigo-500 focus:ring-2 focus:ring-indigo-200 outline-none transition-all`}
              placeholder="Enter your complete address"
            />
            {errors.address && (
              <p className="text-red-500 text-xs flex items-center gap-1">
                <AlertCircle className="w-3 h-3" />
                {errors.address}
              </p>
            )}
          </div>

          <div className="space-y-2">
            <label className="flex items-center gap-2 text-sm font-medium text-gray-700">
              <AlertCircle className="w-4 h-4 text-indigo-600" />
              Issue Type 
            </label>
            <select
              name="issueType"
              value={formData.issueType}
              onChange={handleInputChange}
              required
              className={`w-full px-4 py-3 bg-gray-50 border-2 ${
                errors.issueType ? 'border-red-300' : 'border-gray-200'
              } rounded-xl focus:border-indigo-500 focus:ring-2 focus:ring-indigo-200 outline-none transition-all`}
            >
              <option value="">Select issue type</option>
              {issueTypes.map(type => (
                <option key={type.value} value={type.value}>{type.label}</option>
              ))}
            </select>
            {errors.issueType && (
              <p className="text-red-500 text-xs flex items-center gap-1">
                <AlertCircle className="w-3 h-3" />
                {errors.issueType}
              </p>
            )}
          </div>

          <div className="space-y-2">
            <label className="text-sm font-medium text-gray-700">
              Description 
            </label>
            <textarea
              name="description"
              value={formData.description}
              onChange={handleInputChange}
              required
              rows="4"
              className={`w-full px-4 py-3 bg-gray-50 border-2 ${
                errors.description ? 'border-red-300' : 'border-gray-200'
              } rounded-xl focus:border-indigo-500 focus:ring-2 focus:ring-indigo-200 outline-none transition-all resize-none`}
              placeholder="Describe your issue in detail..."
            />
            {errors.description && (
              <p className="text-red-500 text-xs flex items-center gap-1">
                <AlertCircle className="w-3 h-3" />
                {errors.description}
              </p>
            )}
          </div>

          {errors.submit && (
            <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-xl flex items-center gap-2">
              <AlertCircle className="w-5 h-5" />
              <p className="text-sm">{errors.submit}</p>
            </div>
          )}

          <button
            type="button"
            onClick={handleSubmit}
            disabled={loading}
            className="w-full bg-gradient-to-r from-indigo-600 to-purple-600 text-white py-4 rounded-xl font-semibold shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
          >
            {loading ? (
              <>
                <Clock className="w-5 h-5 animate-spin" />
                Submitting...
              </>
            ) : (
              <>
                <Send className="w-5 h-5" />
                Submit Complaint
              </>
            )}
          </button>
        </div>
      </div>

      {complaints.length > 0 && (
        <div className="bg-white rounded-3xl shadow-xl p-6 space-y-4">
          <h3 className="text-lg font-bold text-gray-800 flex items-center gap-2">
            <Clock className="w-5 h-5 text-indigo-600" />
            Recent Complaints
          </h3>
          <div className="space-y-3">
            {complaints.slice(0, 3).map(complaint => (
              <div key={complaint.id} className="bg-gradient-to-r from-gray-50 to-gray-100 p-4 rounded-xl border-l-4 border-indigo-500">
                <div className="flex items-start justify-between mb-2">
                  <div>
                    <p className="font-semibold text-gray-800">{complaint.name}</p>
                    <p className="text-sm text-gray-600">{issueTypes.find(t => t.value === complaint.issueType)?.label}</p>
                  </div>
                  <span className={`px-3 py-1 rounded-full text-xs font-semibold ${getStatusColor(complaint.status)} text-white`}>
                    {complaint.status}
                  </span>
                </div>
                <div className="flex items-center gap-4 text-xs text-gray-500">
                  <span className="flex items-center gap-1">
                    <Calendar className="w-3 h-3" />
                    {complaint.timestamp}
                  </span>
                  <span className={`font-semibold ${getPriorityColor(complaint.priority)}`}>
                    {complaint.priority.toUpperCase()}
                  </span>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="bg-gradient-to-r from-blue-50 to-indigo-50 rounded-2xl p-5 border border-blue-200">
        <div className="flex gap-3">
          <AlertCircle className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
          <div className="text-sm text-gray-700">
            <p className="font-semibold text-blue-900 mb-1">Quick Response</p>
            <p>Our team typically responds to complaints within 2-4 hours during business hours.</p>
          </div>
        </div>
      </div>

    
    </div>
  );

  const renderServices = () => (
    <div className="space-y-4 pb-24">
      <h2 className="text-2xl font-bold text-gray-800 mb-4">Our Services</h2>
      {[
        { icon: Wifi, title: 'High-Speed Internet', desc: 'Up to 1000 Mbps unlimited data', color: 'from-blue-500 to-cyan-500' },
        { icon: ShoppingBag, title: 'Business Plans', desc: 'Dedicated connections for businesses', color: 'from-purple-500 to-pink-500' },
        { icon: Headphones, title: '24/7 Support', desc: 'Round the clock customer support', color: 'from-green-500 to-emerald-500' },
        { icon: AlertCircle, title: 'Quick Resolution', desc: 'Fast complaint resolution system', color: 'from-orange-500 to-red-500' }
      ].map((service, idx) => (
        <div key={idx} className={`bg-gradient-to-r ${service.color} text-white p-6 rounded-2xl shadow-lg`}>
          <service.icon className="w-10 h-10 mb-3" />
          <h3 className="text-xl font-bold mb-2">{service.title}</h3>
          <p className="text-white/90">{service.desc}</p>
        </div>
      ))}
    </div>
  );

  const renderOffers = () => (
    <div className="space-y-4 pb-24">
      <h2 className="text-2xl font-bold text-gray-800 mb-4">Special Offers</h2>
      {[
     
        { title: 'Annual Plan', discount: '2 Months Free', desc: 'For 10 Months Recharge, get 2 months free', badge: 'Best Value' }
      ].map((offer, idx) => (
        <div key={idx} className="bg-white rounded-2xl shadow-xl p-6 border-2 border-indigo-100">
          <div className="flex justify-between items-start mb-3">
            <Gift className="w-8 h-8 text-indigo-600" />
            <span className="bg-gradient-to-r from-indigo-500 to-purple-500 text-white text-xs font-bold px-3 py-1 rounded-full">
              {offer.badge}
            </span>
          </div>
          <h3 className="text-xl font-bold text-gray-800 mb-1">{offer.title}</h3>
          <p className="text-3xl font-bold text-indigo-600 mb-2">{offer.discount}</p>
          <p className="text-gray-600">{offer.desc}</p>
          <button className="w-full mt-4 bg-gradient-to-r from-indigo-600 to-purple-600 text-white py-3 rounded-xl font-semibold shadow-md hover:shadow-lg transition-all">
            Claim Offer
          </button>
        </div>
      ))}
    </div>
  );

  const renderContact = () => (
    <div className="space-y-4 pb-24">
      <h2 className="text-2xl font-bold text-gray-800 mb-4">Contact Us</h2>
      <div className="bg-white rounded-2xl shadow-xl p-6 space-y-4">
        <div className="flex items-center gap-4 p-4 bg-gradient-to-r from-blue-50 to-indigo-50 rounded-xl">
          <div className="bg-indigo-500 p-3 rounded-full">
            <Phone className="w-6 h-6 text-white" />
          </div>
          <div>
            <p className="text-sm text-gray-600">Call Us</p>
            <p className="text-lg font-bold text-gray-800">+91 7722039108</p>
          </div>
        </div>
        
        <div className="flex items-center gap-4 p-4 bg-gradient-to-r from-purple-50 to-pink-50 rounded-xl">
          <div className="bg-purple-500 p-3 rounded-full">
            <Mail className="w-6 h-6 text-white" />
          </div>
          <div>
            <p className="text-sm text-gray-600">Email Us</p>
            <p className="text-lg font-bold text-gray-800">jainnet1008@gmail.com</p>
          </div>
        </div>

        <div className="flex items-center gap-4 p-4 bg-gradient-to-r from-green-50 to-emerald-50 rounded-xl">
          <div className="bg-green-500 p-3 rounded-full">
            <MessageCircle className="w-6 h-6 text-white" />
          </div>
          <div>
            <p className="text-sm text-gray-600">WhatsApp</p>
            <p className="text-lg font-bold text-gray-800">+91 7722039108</p>
          </div>
        </div>
      </div>

      <div className="bg-white rounded-2xl shadow-xl p-6">
        <h3 className="font-bold text-gray-800 mb-3">Office Address</h3>
        <p className="text-gray-600">
          Jain Internet Services<br />
         New Mondha, Vip Road<br />
          Vishnu Nagar Nanded<br />
          Maharastra - 431602
        </p>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-gradient-to-br from-indigo-50 via-purple-50 to-pink-50">
      <div className="bg-gradient-to-r from-indigo-600 to-purple-600 text-white shadow-lg fixed top-0 left-0 right-0 z-40">
        <div className="max-w-md mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="bg-white/20 backdrop-blur-sm p-2 rounded-lg">
                 <img src="https://app.jaininternet.com/logo-white.png" width="25"/>
              </div>
              <div>
                <h1 className="text-lg font-bold">Jain Internet Services</h1>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <a href="tel:7722039108" className="bg-white/20 backdrop-blur-sm p-2 rounded-lg hover:bg-white/30 transition-all">
                <Phone className="w-5 h-5" />
              </a>
              <button 
                onClick={() => setSidebarOpen(true)}
                className="bg-white/20 backdrop-blur-sm p-2 rounded-lg hover:bg-white/30 transition-all"
              >
                <Menu className="w-5 h-5" />
              </button>
            </div>
          </div>
        </div>
      </div>

    

      <div className={`fixed inset-0 z-50 transition-all duration-300 ${sidebarOpen ? 'visible' : 'invisible'}`}>
        <div 
          className={`absolute inset-0 bg-black transition-opacity duration-300 ${sidebarOpen ? 'opacity-50' : 'opacity-0'}`}
          onClick={() => setSidebarOpen(false)}
        />
        <div className={`absolute right-0 top-0 bottom-0 w-72 bg-white shadow-2xl transform transition-transform duration-300 ${sidebarOpen ? 'translate-x-0' : 'translate-x-full'}`}>
          <div className="p-6">
            <div className="flex items-center justify-between mb-8">
              <h2 className="text-xl font-bold text-gray-800">Menu</h2>
              <button 
                onClick={() => setSidebarOpen(false)}
                className="p-2 hover:bg-gray-100 rounded-lg transition-all"
              >
                <X className="w-6 h-6 text-gray-600" />
              </button>
            </div>
            
            <div className="space-y-2">
              <button 
                onClick={() => { setSidebarOpen(false); }}
                className="w-full flex items-center gap-3 p-4 bg-gradient-to-r from-indigo-50 to-purple-50 rounded-xl hover:from-indigo-100 hover:to-purple-100 transition-all"
              >
                <Headphones className="w-5 h-5 text-indigo-600" />
                <span className="font-semibold text-gray-800">24/7 Support</span>
              </button>
              
              <button 
                onClick={() => { setSidebarOpen(false); setActiveTab('contact'); }}
                className="w-full flex items-center gap-3 p-4 bg-gradient-to-r from-blue-50 to-cyan-50 rounded-xl hover:from-blue-100 hover:to-cyan-100 transition-all"
              >
                <MessageCircle className="w-5 h-5 text-blue-600" />
                <span className="font-semibold text-gray-800">Contact Us</span>
              </button>

              <button 
                onClick={() => { setSidebarOpen(false); }}
                className="w-full flex items-center gap-3 p-4 bg-gradient-to-r from-green-50 to-emerald-50 rounded-xl hover:from-green-100 hover:to-emerald-100 transition-all"
              >
                <Phone className="w-5 h-5 text-green-600" />
                <span className="font-semibold text-gray-800">Call Now</span>
              </button>
            </div>

            <div className="mt-8 p-4 bg-gradient-to-r from-orange-50 to-red-50 rounded-xl">
              <p className="text-sm font-semibold text-orange-900 mb-1">Need Help?</p>
              <p className="text-xs text-orange-700">Our support team is available 24/7 to assist you</p>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-md mx-auto px-4 pt-20 pb-4">
        {activeTab === 'home' && renderHome()}
        {activeTab === 'services' && renderServices()}
        {activeTab === 'offers' && renderOffers()}
        {activeTab === 'contact' && renderContact()}
      </div>

      <div className="fixed bottom-0 left-0 right-0 bg-white border-t border-gray-200 shadow-lg z-30">
        <div className="max-w-md mx-auto px-2 py-2">
          <div className="flex items-center justify-around">
            {[
              { id: 'home', icon: Home, label: 'Home' },
              { id: 'services', icon: ShoppingBag, label: 'Services' },
              { id: 'offers', icon: Gift, label: 'Offers' },
              { id: 'contact', icon: MessageCircle, label: 'Contact' }
            ].map(tab => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`flex flex-col items-center gap-1 py-2 px-4 rounded-xl transition-all ${
                  activeTab === tab.id
                    ? 'bg-gradient-to-r from-indigo-500 to-purple-500 text-white shadow-lg scale-105'
                    : 'text-gray-500 hover:text-indigo-600'
                }`}
              >
                <tab.icon className="w-5 h-5" />
                <span className="text-xs font-medium">{tab.label}</span>
              </button>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}